import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Paper } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DepartmentService } from '../../services/department.service';
import { Department } from '../../interfaces/department.interface';
import { useToast } from '../../context/ToastContext';

const DepartmentForm: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const existingDepartment = location.state?.department as Department | undefined;

    const [formData, setFormData] = useState({
        name: existingDepartment?.name || '',
        description: existingDepartment?.description || ''
    });
    const toast = useToast();
    const { t } = useTranslation(['department', 'common']);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (existingDepartment) {
                await DepartmentService.updateDepartment(existingDepartment.id, formData);
            } else {
                await DepartmentService.createDepartment(formData);
            }
            navigate('/departments');
        } catch (err) {
            toast.showApiError(err, t('departmentForm.saveFailed'));
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    return (
        <Box sx={{ padding: 3 }}>
            <Paper sx={{ padding: 3, maxWidth: 600, margin: '0 auto' }}>
                <Typography variant="h5" sx={{ marginBottom: 3 }}>
                    {existingDepartment ? t('departmentForm.editTitle') : t('departmentForm.createTitle')}
                </Typography>
                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        label={t('common:fields.name')}
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        sx={{ marginBottom: 2 }}
                    />
                    <TextField
                        fullWidth
                        label={t('common:fields.description')}
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        multiline
                        rows={4}
                        sx={{ marginBottom: 3 }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                        >
                            {existingDepartment ? t('common:actions.update') : t('common:actions.create')}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/departments')}
                        >
                            {t('common:actions.cancel')}
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
};

export default DepartmentForm;
